import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import journeysService from "../services/journeysService";
import campaignService from "../services/campaignService";
import segmentsService from "../services/segmentsService";
import { Journey, JourneyStep, JourneyEnrollment, StepExecution } from "../types/journey";
import { NewsletterEdition } from "../types/newsletterEdition";
import { Segment } from "../types/segment";
import { selectOrganization } from "../store/slices/authUserSlice";

const statusColors: Record<string, string> = {
  draft: "#6b7280",
  active: "#22c55e",
  paused: "#f59e0b",
  archived: "#9ca3af",
};

const JourneyDetail: React.FC = () => {
  const { journeyId } = useParams<{ journeyId: string }>();
  const navigate = useNavigate();
  const organization = useSelector(selectOrganization);

  const [journey, setJourney] = useState<Journey | null>(null);
  const [enrollments, setEnrollments] = useState<JourneyEnrollment[]>([]);
  const [enrollmentsPage, setEnrollmentsPage] = useState(1);
  const [enrollmentsTotalPages, setEnrollmentsTotalPages] = useState(1);
  const [editions, setEditions] = useState<NewsletterEdition[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"steps" | "enrollments">("steps");
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState("");
  const [copied, setCopied] = useState(false);

  // New step form
  const [showAddStep, setShowAddStep] = useState(false);
  const [newStepType, setNewStepType] = useState<"email" | "cancel_journey" | "add_to_segment" | "remove_from_segment">("email");
  const [newStepEditionId, setNewStepEditionId] = useState("");
  const [newStepCancelsJourneyId, setNewStepCancelsJourneyId] = useState("");
  const [newStepSegmentId, setNewStepSegmentId] = useState("");
  const [newStepDelayAmount, setNewStepDelayAmount] = useState(0);
  const [newStepDelayUnit, setNewStepDelayUnit] = useState("hours");
  const [allJourneys, setAllJourneys] = useState<Journey[]>([]);

  // Manual enrollment
  const [showEnrollForm, setShowEnrollForm] = useState(false);
  const [enrollEmail, setEnrollEmail] = useState("");
  const [enrolling, setEnrolling] = useState(false);
  const [enrollMessage, setEnrollMessage] = useState<{ text: string; error: boolean } | null>(null);

  // Segment enrollment
  const [showEnrollSegmentForm, setShowEnrollSegmentForm] = useState(false);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [selectedSegmentId, setSelectedSegmentId] = useState("");
  const [enrollingSegment, setEnrollingSegment] = useState(false);

  // Inline delay editing
  const [editingDelayStepId, setEditingDelayStepId] = useState<string | null>(null);
  const [editDelayAmount, setEditDelayAmount] = useState(0);
  const [editDelayUnit, setEditDelayUnit] = useState("hours");

  // Step executions expansion
  const [expandedStepId, setExpandedStepId] = useState<string | null>(null);
  const [stepExecutions, setStepExecutions] = useState<StepExecution[]>([]);
  const [loadingExecutions, setLoadingExecutions] = useState(false);
  const [showAllExecutions, setShowAllExecutions] = useState(false);

  const loadJourney = useCallback(async () => {
    if (!journeyId) return;
    try {
      const data = await journeysService.getJourney(journeyId);
      setJourney(data);
      setNameValue(data.name);
    } catch (error) {
      console.error("Failed to load journey:", error);
    } finally {
      setLoading(false);
    }
  }, [journeyId]);

  const loadEditions = useCallback(async () => {
    try {
      const data = await campaignService.listNewsletterEditions();
      setEditions(data);
    } catch (error) {
      console.error("Failed to load editions:", error);
    }
  }, []);

  const loadEnrollments = useCallback(async (page: number = 1) => {
    if (!journeyId) return;
    try {
      const data = await journeysService.getEnrollments(journeyId, page);
      setEnrollments(data.enrollments);
      setEnrollmentsPage(data.current_page);
      setEnrollmentsTotalPages(data.total_pages);
    } catch (error) {
      console.error("Failed to load enrollments:", error);
    }
  }, [journeyId]);

  const loadAllJourneys = useCallback(async () => {
    try {
      const data = await journeysService.listJourneys();
      setAllJourneys(data);
    } catch (error) {
      console.error("Failed to load journeys:", error);
    }
  }, []);

  const loadSegments = useCallback(async () => {
    try {
      const data = await segmentsService.listSegments();
      setSegments(data);
    } catch (error) {
      console.error("Failed to load segments:", error);
    }
  }, []);

  const loadExecutions = async (stepId: string, showAll: boolean) => {
    setLoadingExecutions(true);
    try {
      const status = showAll ? undefined : "pending";
      const data = await journeysService.getStepExecutions(journeyId!, stepId, status);
      setStepExecutions(data.executions);
    } catch (error) {
      console.error("Failed to load executions:", error);
      setStepExecutions([]);
    } finally {
      setLoadingExecutions(false);
    }
  };

  const handleStepClick = async (step: JourneyStep) => {
    if (step.step_type !== "email") return;
    if (expandedStepId === step.id) {
      setExpandedStepId(null);
      setStepExecutions([]);
      setShowAllExecutions(false);
      return;
    }
    setExpandedStepId(step.id);
    setShowAllExecutions(false);
    await loadExecutions(step.id, false);
  };

  const handleToggleShowAll = async (stepId: string) => {
    const newShowAll = !showAllExecutions;
    setShowAllExecutions(newShowAll);
    await loadExecutions(stepId, newShowAll);
  };

  useEffect(() => {
    loadJourney();
    loadEditions();
    loadAllJourneys();
    loadSegments();
  }, [loadJourney, loadEditions, loadAllJourneys, loadSegments]);

  useEffect(() => {
    if (activeTab === "enrollments") {
      loadEnrollments(1);
    }
  }, [activeTab, loadEnrollments]);

  const handleEnrollSubscriber = async () => {
    if (!journey || !enrollEmail.trim()) return;
    setEnrolling(true);
    setEnrollMessage(null);
    try {
      const result = await journeysService.enrollSubscriber(journey.id, enrollEmail.trim());
      setEnrollMessage({ text: result.message, error: false });
      setEnrollEmail("");
      setShowEnrollForm(false);
      await loadEnrollments(1);
    } catch (error: any) {
      const msg = error?.response?.data?.error || "Failed to enroll subscriber";
      setEnrollMessage({ text: msg, error: true });
      // A validation failure is recorded as a failed_validation enrollment
      await loadEnrollments(1);
    } finally {
      setEnrolling(false);
    }
  };

  const handleEnrollSegment = async () => {
    if (!journey || !selectedSegmentId) return;
    setEnrollingSegment(true);
    setEnrollMessage(null);
    try {
      const result = await journeysService.enrollSegment(journey.id, selectedSegmentId);
      setEnrollMessage({ text: result.message, error: false });
      setSelectedSegmentId("");
      setShowEnrollSegmentForm(false);
      await loadEnrollments(1);
    } catch (error: any) {
      const msg = error?.response?.data?.error || "Failed to enroll segment";
      setEnrollMessage({ text: msg, error: true });
    } finally {
      setEnrollingSegment(false);
    }
  };

  const handleNameSave = async () => {
    if (!journey || !nameValue.trim()) return;
    try {
      const updated = await journeysService.updateJourney(journey.id, nameValue.trim());
      setJourney(updated);
      setEditingName(false);
    } catch (error) {
      console.error("Failed to update name:", error);
    }
  };

  const handleActivate = async () => {
    if (!journey) return;
    try {
      const updated = await journeysService.activateJourney(journey.id);
      setJourney(updated);
    } catch (error) {
      console.error("Failed to activate:", error);
    }
  };

  const handlePause = async () => {
    if (!journey) return;
    try {
      const updated = await journeysService.pauseJourney(journey.id);
      setJourney(updated);
    } catch (error) {
      console.error("Failed to pause:", error);
    }
  };

  const handleArchive = async () => {
    if (!journey) return;
    if (!window.confirm("Are you sure you want to archive this journey?")) return;
    try {
      const updated = await journeysService.archiveJourney(journey.id);
      setJourney(updated);
    } catch (error) {
      console.error("Failed to archive:", error);
    }
  };

  const handleSetWelcome = async () => {
    if (!journey) return;
    if (!window.confirm("Set this journey as the welcome sequence? New signups will be enrolled in this journey instead of receiving the launch email.")) return;
    try {
      const updated = await journeysService.setWelcomeJourney(journey.id);
      setJourney(updated);
    } catch (error: any) {
      window.alert(error?.response?.data?.error || "Failed to set welcome sequence");
    }
  };

  const handleUnsetWelcome = async () => {
    if (!journey) return;
    if (!window.confirm("Remove this journey as the welcome sequence? New signups will receive the launch email again.")) return;
    try {
      const updated = await journeysService.unsetWelcomeJourney(journey.id);
      setJourney(updated);
    } catch (error: any) {
      window.alert(error?.response?.data?.error || "Failed to remove welcome sequence");
    }
  };

  const handleDelete = async () => {
    if (!journey) return;
    if (!window.confirm("Are you sure you want to delete this journey? This cannot be undone.")) return;
    try {
      await journeysService.deleteJourney(journey.id);
      navigate("/journeys");
    } catch (error) {
      console.error("Failed to delete:", error);
    }
  };

  const handleCopyWebhook = () => {
    if (!journey?.webhook_url) return;
    navigator.clipboard.writeText(journey.webhook_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleAddStep = async () => {
    if (!journey) return;
    if (newStepType === "email" && !newStepEditionId) return;
    if (newStepType === "cancel_journey" && !newStepCancelsJourneyId) return;
    if ((newStepType === "add_to_segment" || newStepType === "remove_from_segment") && !newStepSegmentId) return;
    try {
      await journeysService.createStep(journey.id, {
        step_type: newStepType,
        ...(newStepType === "email"
          ? { newsletter_edition_id: newStepEditionId }
          : newStepType === "cancel_journey"
          ? { cancels_journey_id: newStepCancelsJourneyId }
          : { segment_id: newStepSegmentId }),
        position: (journey.steps?.length || 0),
        delay_amount: newStepDelayAmount,
        delay_unit: newStepDelayUnit,
      });
      setShowAddStep(false);
      setNewStepType("email");
      setNewStepEditionId("");
      setNewStepCancelsJourneyId("");
      setNewStepSegmentId("");
      setNewStepDelayAmount(0);
      setNewStepDelayUnit("hours");
      await loadJourney();
    } catch (error) {
      console.error("Failed to add step:", error);
    }
  };

  const handleDeleteStep = async (stepId: string) => {
    if (!journey) return;
    if (!window.confirm("Delete this step?")) return;
    try {
      await journeysService.deleteStep(journey.id, stepId);
      await loadJourney();
    } catch (error) {
      console.error("Failed to delete step:", error);
    }
  };

  const handleSaveDelay = async (stepId: string) => {
    if (!journey) return;
    try {
      await journeysService.updateStep(journey.id, stepId, {
        delay_amount: editDelayAmount,
        delay_unit: editDelayUnit,
      });
      setEditingDelayStepId(null);
      await loadJourney();
    } catch (error) {
      console.error("Failed to update delay:", error);
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination || !journey?.steps) return;
    if (result.source.index === result.destination.index) return;

    const reordered = Array.from(journey.steps);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    // Optimistic update
    setJourney({ ...journey, steps: reordered });

    try {
      await journeysService.reorderSteps(
        journey.id,
        reordered.map((s) => s.id)
      );
    } catch (error) {
      console.error("Failed to reorder steps:", error);
      await loadJourney(); // Revert on failure
    }
  };

  const stepTargetMissing =
    (newStepType === "email" && !newStepEditionId) ||
    (newStepType === "cancel_journey" && !newStepCancelsJourneyId) ||
    ((newStepType === "add_to_segment" || newStepType === "remove_from_segment") && !newStepSegmentId);

  if (loading) {
    return <div style={{ padding: "20px" }}>Loading...</div>;
  }

  if (!journey) {
    return <div style={{ padding: "20px" }}>Journey not found.</div>;
  }

  return (
    <div style={{ padding: "20px", maxWidth: "900px" }}>
      {/* Header */}
      <div style={{ marginBottom: "20px" }}>
        <button
          onClick={() => navigate("/journeys")}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#6b7280",
            padding: 0,
            marginBottom: "8px",
          }}
        >
          &larr; Back to Journeys
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {editingName ? (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <input
                type="text"
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleNameSave()}
                style={{
                  fontSize: "24px",
                  fontWeight: 700,
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  padding: "4px 8px",
                }}
                autoFocus
              />
              <button
                onClick={handleNameSave}
                style={{
                  padding: "4px 12px",
                  backgroundColor: "#000",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Save
              </button>
              <button
                onClick={() => {
                  setEditingName(false);
                  setNameValue(journey.name);
                }}
                style={{
                  padding: "4px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  backgroundColor: "#fff",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <h1
              style={{ margin: 0, cursor: "pointer" }}
              onClick={() => setEditingName(true)}
              title="Click to edit"
            >
              {journey.name}
            </h1>
          )}
          <span
            style={{
              padding: "4px 12px",
              borderRadius: "12px",
              fontSize: "12px",
              fontWeight: 600,
              color: "#fff",
              backgroundColor: statusColors[journey.status] || "#6b7280",
            }}
          >
            {journey.status}
          </span>
        </div>

        {journey.description && (
          <p style={{ margin: "8px 0 0 0", color: "#6b7280", fontSize: "14px" }}>
            {journey.description}
          </p>
        )}
      </div>

      {/* Welcome fallback warning */}
      {journey.welcome_fallback_active && (
        <div
          style={{
            padding: "10px 14px",
            marginBottom: "16px",
            borderRadius: "4px",
            fontSize: "13px",
            backgroundColor: "#fef3c7",
            color: "#92400e",
            border: "1px solid #fcd34d",
          }}
        >
          This is the welcome sequence but it isn't active — new signups are receiving
          the launch email instead. Note: pausing also cancelled any in-progress
          enrollments.
        </div>
      )}

      {/* Status Controls */}
      <div
        style={{
          display: "flex",
          gap: "8px",
          marginBottom: enrollMessage ? "8px" : "20px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {(journey.status === "draft" || journey.status === "paused") && (
          <button
            onClick={handleActivate}
            style={{
              padding: "6px 14px",
              backgroundColor: "#22c55e",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Activate
          </button>
        )}
        {journey.status === "active" && (
          <button
            onClick={handlePause}
            style={{
              padding: "6px 14px",
              backgroundColor: "#fff",
              color: "#374151",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Pause
          </button>
        )}
        {journey.status !== "archived" && (
          <button
            onClick={handleArchive}
            style={{
              padding: "6px 14px",
              backgroundColor: "#9ca3af",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Archive
          </button>
        )}
        {journey.status === "active" && !journey.is_welcome_journey && (
          <button
            onClick={handleSetWelcome}
            style={{
              padding: "6px 14px",
              backgroundColor: "#fff",
              color: "#374151",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Set as Welcome Sequence
          </button>
        )}
        {journey.is_welcome_journey && (
          <button
            onClick={handleUnsetWelcome}
            style={{
              padding: "6px 14px",
              backgroundColor: "#fff",
              color: "#374151",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Remove as Welcome Sequence
          </button>
        )}
        <button
          onClick={handleDelete}
          style={{
            padding: "6px 14px",
            backgroundColor: "#fff",
            color: "#374151",
            border: "1px solid #d1d5db",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Delete
        </button>
        <button
          onClick={() => { setShowEnrollForm(!showEnrollForm); setShowEnrollSegmentForm(false); setEnrollMessage(null); setEnrollEmail(""); }}
          style={{
            marginLeft: "auto",
            padding: "6px 14px",
            backgroundColor: "#fff",
            color: "#000",
            border: "1px solid #000",
            borderRadius: "1px",
            cursor: "pointer",
          }}
        >
          + Add Subscriber
        </button>
        <button
          onClick={() => { setShowEnrollSegmentForm(!showEnrollSegmentForm); setShowEnrollForm(false); setEnrollMessage(null); setSelectedSegmentId(""); }}
          style={{
            padding: "6px 14px",
            backgroundColor: "#fff",
            color: "#000",
            border: "1px solid #000",
            borderRadius: "1px",
            cursor: "pointer",
          }}
        >
          + Enroll Segment
        </button>
      </div>

      {/* Enroll feedback */}
      {enrollMessage && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: "20px",
            borderRadius: "4px",
            fontSize: "13px",
            backgroundColor: enrollMessage.error ? "#fee2e2" : "#dcfce7",
            color: enrollMessage.error ? "#991b1b" : "#166534",
          }}
        >
          {enrollMessage.text}
        </div>
      )}

      {/* Inline enroll form */}
      {showEnrollForm && (
        <div
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
            padding: "12px 16px",
            backgroundColor: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            marginBottom: "20px",
          }}
        >
          <input
            type="email"
            placeholder="subscriber@example.com"
            value={enrollEmail}
            onChange={(e) => setEnrollEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleEnrollSubscriber()}
            autoFocus
            style={{
              flex: 1,
              padding: "6px 10px",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
              fontSize: "14px",
            }}
          />
          <button
            onClick={handleEnrollSubscriber}
            disabled={enrolling || !enrollEmail.trim()}
            style={{
              padding: "6px 14px",
              backgroundColor: "#000",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: enrolling || !enrollEmail.trim() ? "not-allowed" : "pointer",
              opacity: enrolling || !enrollEmail.trim() ? 0.5 : 1,
              fontSize: "13px",
            }}
          >
            {enrolling ? "Enrolling..." : "Enroll"}
          </button>
          <button
            onClick={() => { setShowEnrollForm(false); setEnrollEmail(""); setEnrollMessage(null); }}
            style={{
              padding: "6px 14px",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
              backgroundColor: "#fff",
              cursor: "pointer",
              fontSize: "13px",
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Inline segment enroll form */}
      {showEnrollSegmentForm && !organization?.feature_flags?.segment_sending_enabled && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: "20px",
            borderRadius: "4px",
            fontSize: "13px",
            backgroundColor: "#fef9c3",
            color: "#713f12",
          }}
        >
          Segment enrollment is not enabled for your account. Contact support to enable it.
        </div>
      )}
      {showEnrollSegmentForm && organization?.feature_flags?.segment_sending_enabled && (
        <div
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
            padding: "12px 16px",
            backgroundColor: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            marginBottom: "20px",
          }}
        >
          <select
            value={selectedSegmentId}
            onChange={(e) => setSelectedSegmentId(e.target.value)}
            autoFocus
            style={{
              flex: 1,
              padding: "6px 10px",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
              fontSize: "14px",
              backgroundColor: "#fff",
            }}
          >
            <option value="">Select a segment...</option>
            {segments.map((seg) => (
              <option key={seg.id} value={seg.id}>
                {seg.name}{seg.subscriber_count != null ? ` (${seg.subscriber_count} subscribers)` : ""}
              </option>
            ))}
          </select>
          <button
            onClick={handleEnrollSegment}
            disabled={enrollingSegment || !selectedSegmentId}
            style={{
              padding: "6px 14px",
              backgroundColor: "#7c3aed",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: enrollingSegment || !selectedSegmentId ? "not-allowed" : "pointer",
              opacity: enrollingSegment || !selectedSegmentId ? 0.5 : 1,
              fontSize: "13px",
            }}
          >
            {enrollingSegment ? "Enrolling..." : "Enroll Segment"}
          </button>
          <button
            onClick={() => { setShowEnrollSegmentForm(false); setSelectedSegmentId(""); setEnrollMessage(null); }}
            style={{
              padding: "6px 14px",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
              backgroundColor: "#fff",
              cursor: "pointer",
              fontSize: "13px",
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Webhook URL */}
      <div
        style={{
          padding: "12px 16px",
          backgroundColor: "#f3f4f6",
          borderRadius: "8px",
          marginBottom: "20px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontWeight: 600, fontSize: "13px" }}>Webhook URL</span>
          <code
            style={{
              flex: 1,
              fontSize: "13px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {journey.webhook_url}
          </code>
          <button
            onClick={handleCopyWebhook}
            style={{
              padding: "4px 12px",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
              backgroundColor: "#fff",
              cursor: "pointer",
              fontSize: "12px",
              flexShrink: 0,
            }}
          >
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>
        <p style={{ margin: "6px 0 0 0", fontSize: "12px", color: "#6b7280" }}>
          POST to this URL with an <code>email</code> field to automatically enroll a subscriber in this journey. Use it with tools like Zapier, Clay, or your own backend.
        </p>
      </div>

      {/* Blueprint Link */}
      {journey.blueprint_id && (
        <div
          style={{
            padding: "12px 16px",
            backgroundColor: "#f3f4f6",
            borderRadius: "8px",
            marginBottom: "20px",
            fontSize: "13px",
          }}
        >
          <span style={{ fontWeight: 600 }}>Blueprint: </span>
          <a
            href={`/blueprints/${journey.blueprint_id}`}
            onClick={(e) => {
              e.preventDefault();
              navigate(`/blueprints/${journey.blueprint_id}`);
            }}
            style={{ color: "#2563eb", textDecoration: "none" }}
          >
            {journey.blueprint_title || `Blueprint ${journey.blueprint_id}`} &rarr;
          </a>
        </div>
      )}

      {/* Tabs */}
      <div
        style={{
          display: "flex",
          gap: "0",
          borderBottom: "2px solid #e5e7eb",
          marginBottom: "20px",
        }}
      >
        {(["steps", "enrollments"] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: "8px 20px",
              border: "none",
              backgroundColor: "transparent",
              cursor: "pointer",
              fontWeight: activeTab === tab ? 700 : 400,
              borderBottom:
                activeTab === tab ? "2px solid #000" : "2px solid transparent",
              marginBottom: "-2px",
              textTransform: "capitalize",
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Data propagation disclaimer */}
      <div
        style={{
          padding: "10px 14px",
          backgroundColor: "#f9fafb",
          border: "1px solid #e5e7eb",
          borderRadius: "6px",
          marginBottom: "16px",
          fontSize: "12px",
          color: "#9ca3af",
        }}
      >
        Analytics may take up to 24 hours to propagate.
      </div>

      {/* Steps Tab */}
      {activeTab === "steps" && (
        <div>
          {journey.steps && journey.steps.length > 0 ? (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="journey-steps">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={{ display: "flex", flexDirection: "column", gap: "8px" }}
                  >
                    {journey.steps!.map((step, index) => (
                      <Draggable key={step.id} draggableId={String(step.id)} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                          >
                            <div
                              style={{
                                padding: "16px 20px",
                                backgroundColor: snapshot.isDragging ? "#f9fafb" : "#fff",
                                border: "1px solid #e5e7eb",
                                borderLeft: step.step_type === "cancel_journey" ? "3px solid #d1d5db" : (step.step_type === "add_to_segment" || step.step_type === "remove_from_segment") ? "3px solid #16a34a" : "3px solid #2563eb",
                                borderRadius: "8px",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: "16px",
                                boxShadow: snapshot.isDragging ? "0 4px 12px rgba(0,0,0,0.15)" : "none",
                              }}
                            >
                              {/* Drag handle */}
                              <div
                                {...provided.dragHandleProps}
                                style={{
                                  cursor: "grab",
                                  padding: "4px 2px",
                                  color: "#9ca3af",
                                  display: "flex",
                                  alignItems: "center",
                                  flexShrink: 0,
                                }}
                                title="Drag to reorder"
                              >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                  <circle cx="5" cy="3" r="1.5" />
                                  <circle cx="11" cy="3" r="1.5" />
                                  <circle cx="5" cy="8" r="1.5" />
                                  <circle cx="11" cy="8" r="1.5" />
                                  <circle cx="5" cy="13" r="1.5" />
                                  <circle cx="11" cy="13" r="1.5" />
                                </svg>
                              </div>

                              <div style={{ flex: 1, minWidth: 0 }}>
                                {/* Step label + title */}
                                <div style={{ display: "flex", alignItems: "baseline", gap: "6px", flexWrap: "wrap" }}>
                                  <span style={{ fontSize: "11px", fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                    Step {index + 1}
                                  </span>
                                  {step.step_type === "cancel_journey" ? (
                                    <span style={{ fontSize: "14px", fontWeight: 600, color: "#374151" }}>
                                      Cancel: {step.cancels_journey_name || "Unknown Journey"}
                                    </span>
                                  ) : step.step_type === "add_to_segment" ? (
                                    <span style={{ fontSize: "14px", fontWeight: 600, color: "#15803d" }}>
                                      Add to segment: {step.segment_name || "Unknown segment"}
                                    </span>
                                  ) : step.step_type === "remove_from_segment" ? (
                                    <span style={{ fontSize: "14px", fontWeight: 600, color: "#b91c1c" }}>
                                      Remove from segment: {step.segment_name || "Unknown segment"}
                                    </span>
                                  ) : (
                                    <a
                                      href={`/newsletterEdition/${step.newsletter_edition_id}`}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        navigate(`/newsletterEdition/${step.newsletter_edition_id}`);
                                      }}
                                      style={{ fontSize: "14px", fontWeight: 600, color: "#111827", textDecoration: "none" }}
                                    >
                                      {step.newsletter_edition_title || "Unknown Edition"}
                                    </a>
                                  )}
                                </div>

                                {/* Delay + stats row */}
                                <div style={{ display: "flex", alignItems: "center", gap: "16px", marginTop: "6px", flexWrap: "wrap" }}>
                                  {editingDelayStepId === step.id ? (
                                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                      <input
                                        type="number"
                                        min={0}
                                        value={editDelayAmount}
                                        onChange={(e) => setEditDelayAmount(parseInt(e.target.value) || 0)}
                                        style={{ width: "60px", padding: "2px 6px", fontSize: "12px", border: "1px solid #d1d5db", borderRadius: "4px" }}
                                      />
                                      <select
                                        value={editDelayUnit}
                                        onChange={(e) => setEditDelayUnit(e.target.value)}
                                        style={{ padding: "2px 6px", fontSize: "12px", border: "1px solid #d1d5db", borderRadius: "4px" }}
                                      >
                                        <option value="minutes">minutes</option>
                                        <option value="hours">hours</option>
                                        <option value="days">days</option>
                                      </select>
                                      <button
                                        onClick={() => handleSaveDelay(step.id)}
                                        style={{ padding: "2px 8px", fontSize: "12px", border: "1px solid #d1d5db", borderRadius: "4px", backgroundColor: "#2563eb", color: "#fff", cursor: "pointer" }}
                                      >
                                        Save
                                      </button>
                                      <button
                                        onClick={() => setEditingDelayStepId(null)}
                                        style={{ padding: "2px 8px", fontSize: "12px", border: "1px solid #d1d5db", borderRadius: "4px", backgroundColor: "#fff", color: "#6b7280", cursor: "pointer" }}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  ) : (
                                    <span
                                      onClick={() => {
                                        setEditingDelayStepId(step.id);
                                        setEditDelayAmount(step.delay_amount);
                                        setEditDelayUnit(step.delay_unit);
                                      }}
                                      style={{ fontSize: "12px", color: "#6b7280", cursor: "pointer", textDecoration: "underline dotted", textUnderlineOffset: "3px" }}
                                      title="Click to edit delay"
                                    >
                                      {step.delay_amount === 0
                                        ? step.step_type === "email" ? "Send immediately" : "Execute immediately"
                                        : `Wait ${step.delay_amount} ${step.delay_unit}`}
                                    </span>
                                  )}
                                  {step.step_type === "email" && (
                                    <>
                                      <span style={{ fontSize: "12px", color: "#6b7280" }}>
                                        {(step.total_deliveries ?? 0).toLocaleString()} sent
                                      </span>
                                      <span style={{ fontSize: "12px", color: "#6b7280" }}>
                                        {step.open_rate ?? 0}% opens
                                      </span>
                                      <span style={{ fontSize: "12px", color: "#6b7280" }}>
                                        {step.click_rate ?? 0}% clicks
                                      </span>
                                    </>
                                  )}
                                  {step.pending_count > 0 && (
                                    <span style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", color: "#92400e" }}>
                                      <span style={{ width: "7px", height: "7px", borderRadius: "50%", backgroundColor: "#f59e0b", display: "inline-block", flexShrink: 0 }} />
                                      {step.pending_count} in queue
                                      {step.step_type === "email" && (
                                        <button
                                          onClick={() => handleStepClick(step)}
                                          style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: "12px", color: "#92400e", textDecoration: "underline" }}
                                        >
                                          ({expandedStepId === step.id ? "hide" : "see all"})
                                        </button>
                                      )}
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div style={{ display: "flex", gap: "8px", alignItems: "center", flexShrink: 0 }}>
                                <button
                                  onClick={() => handleDeleteStep(step.id)}
                                  style={{
                                    padding: "4px 10px",
                                    border: "1px solid #d1d5db",
                                    borderRadius: "4px",
                                    backgroundColor: "#fff",
                                    color: "#6b7280",
                                    cursor: "pointer",
                                    fontSize: "12px",
                                  }}
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                            {expandedStepId === step.id && (
                    <div
                      style={{
                        border: "1px solid #e5e7eb",
                        borderTop: "none",
                        borderRadius: "0 0 8px 8px",
                        backgroundColor: "#f9fafb",
                        padding: "16px",
                        marginTop: "-4px",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "8px" }}>
                        <button
                          onClick={() => handleToggleShowAll(step.id)}
                          disabled={loadingExecutions}
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: loadingExecutions ? "default" : "pointer",
                            fontSize: "12px",
                            color: "#2563eb",
                            textDecoration: "underline",
                          }}
                        >
                          {showAllExecutions ? "Show Queued Only" : "Show All"}
                        </button>
                      </div>
                      {loadingExecutions ? (
                        <div style={{ textAlign: "center", color: "#6b7280", padding: "20px" }}>
                          Loading subscriber data...
                        </div>
                      ) : stepExecutions.length === 0 ? (
                        <div style={{ textAlign: "center", color: "#6b7280", padding: "20px" }}>
                          No executions yet for this step.
                        </div>
                      ) : (
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            backgroundColor: "#fff",
                            borderRadius: "6px",
                            overflow: "hidden",
                            fontSize: "13px",
                          }}
                        >
                          <thead>
                            <tr style={{ backgroundColor: "#f3f4f6" }}>
                              <th style={{ padding: "8px 12px", textAlign: "left" }}>Email</th>
                              <th style={{ padding: "8px 12px", textAlign: "left" }}>Status</th>
                              <th style={{ padding: "8px 12px", textAlign: "center" }}>Delivered</th>
                              <th style={{ padding: "8px 12px", textAlign: "center" }}>Opened</th>
                              <th style={{ padding: "8px 12px", textAlign: "center" }}>Clicked</th>
                            </tr>
                          </thead>
                          <tbody>
                            {stepExecutions.map((exec, i) => {
                              const statusBadge: Record<string, { bg: string; color: string }> = {
                                sent: { bg: "#f0fdf4", color: "#166534" },
                                failed: { bg: "#fef2f2", color: "#991b1b" },
                                skipped: { bg: "#f3f4f6", color: "#6b7280" },
                                pending: { bg: "#fefce8", color: "#92400e" },
                                scheduled: { bg: "#f0f9ff", color: "#1e40af" },
                              };
                              const badge = statusBadge[exec.execution_status] || statusBadge.pending;
                              return (
                                <tr key={i} style={{ borderTop: "1px solid #e5e7eb" }}>
                                  <td style={{ padding: "8px 12px" }}>
                                    <a
                                      href={`/subscribers/${exec.subscriber_id}`}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        navigate(`/subscribers/${exec.subscriber_id}`);
                                      }}
                                      style={{ color: "#2563eb", textDecoration: "none" }}
                                    >
                                      {exec.email}
                                    </a>
                                  </td>
                                  <td style={{ padding: "8px 12px" }}>
                                    <span
                                      style={{
                                        padding: "2px 8px",
                                        borderRadius: "8px",
                                        fontSize: "11px",
                                        fontWeight: 600,
                                        backgroundColor: badge.bg,
                                        color: badge.color,
                                      }}
                                    >
                                      {exec.execution_status}
                                    </span>
                                  </td>
                                  <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                    {exec.delivered ? "Yes" : "-"}
                                  </td>
                                  <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                    {exec.opened ? "Yes" : "-"}
                                  </td>
                                  <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                    {exec.clicked ? "Yes" : "-"}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                            </div>
                          )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            <p style={{ color: "#6b7280" }}>
              No steps yet. Add a step to define the email sequence.
            </p>
          )}

          {/* Add Step */}
          {showAddStep ? (
            <div
              style={{
                marginTop: "12px",
                padding: "16px",
                backgroundColor: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
              }}
            >
              <h3 style={{ margin: "0 0 12px 0" }}>Add Step</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "4px" }}>
                    Step Type
                  </label>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px" }}>
                    <button
                      type="button"
                      onClick={() => { setNewStepType("email"); setNewStepCancelsJourneyId(""); setNewStepSegmentId(""); }}
                      style={{
                        padding: "8px",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontWeight: newStepType === "email" ? 700 : 400,
                        backgroundColor: newStepType === "email" ? "#000" : "#fff",
                        color: newStepType === "email" ? "#fff" : "#000",
                      }}
                    >
                      Email
                    </button>
                    <button
                      type="button"
                      onClick={() => { setNewStepType("cancel_journey"); setNewStepEditionId(""); setNewStepSegmentId(""); }}
                      style={{
                        padding: "8px",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontWeight: newStepType === "cancel_journey" ? 700 : 400,
                        backgroundColor: newStepType === "cancel_journey" ? "#ea580c" : "#fff",
                        color: newStepType === "cancel_journey" ? "#fff" : "#000",
                      }}
                    >
                      Cancel Journey
                    </button>
                    <button
                      type="button"
                      onClick={() => { setNewStepType("add_to_segment"); setNewStepEditionId(""); setNewStepCancelsJourneyId(""); }}
                      style={{
                        padding: "8px",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontWeight: newStepType === "add_to_segment" ? 700 : 400,
                        backgroundColor: newStepType === "add_to_segment" ? "#16a34a" : "#fff",
                        color: newStepType === "add_to_segment" ? "#fff" : "#000",
                      }}
                    >
                      Add to Segment
                    </button>
                    <button
                      type="button"
                      onClick={() => { setNewStepType("remove_from_segment"); setNewStepEditionId(""); setNewStepCancelsJourneyId(""); }}
                      style={{
                        padding: "8px",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontWeight: newStepType === "remove_from_segment" ? 700 : 400,
                        backgroundColor: newStepType === "remove_from_segment" ? "#dc2626" : "#fff",
                        color: newStepType === "remove_from_segment" ? "#fff" : "#000",
                      }}
                    >
                      Remove from Segment
                    </button>
                  </div>
                </div>
                {newStepType === "email" ? (
                  <div>
                    <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "4px" }}>
                      Newsletter Edition
                    </label>
                    <select
                      value={newStepEditionId}
                      onChange={(e) => setNewStepEditionId(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                      }}
                    >
                      <option value="">Select an edition...</option>
                      {editions.map((edition) => (
                        <option key={edition.id} value={edition.id}>
                          {edition.title}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : newStepType === "cancel_journey" ? (
                  <div>
                    <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "4px" }}>
                      Journey to Cancel
                    </label>
                    <select
                      value={newStepCancelsJourneyId}
                      onChange={(e) => setNewStepCancelsJourneyId(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                      }}
                    >
                      <option value="">Select a journey...</option>
                      {allJourneys
                        .filter((j) => j.id !== journeyId)
                        .map((j) => (
                          <option key={j.id} value={j.id}>
                            {j.name}
                          </option>
                        ))}
                    </select>
                  </div>
                ) : (
                  <div>
                    <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "4px" }}>
                      Segment
                    </label>
                    <select
                      value={newStepSegmentId}
                      onChange={(e) => setNewStepSegmentId(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                      }}
                    >
                      <option value="">Select a static segment...</option>
                      {segments
                        .filter((s) => s.segment_type === "static")
                        .map((seg) => (
                          <option key={seg.id} value={seg.id}>
                            {seg.name}
                          </option>
                        ))}
                    </select>
                  </div>
                )}
                <div style={{ display: "flex", gap: "8px" }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "4px" }}>
                      Delay Amount
                    </label>
                    <input
                      type="number"
                      min={0}
                      value={newStepDelayAmount}
                      onChange={(e) => setNewStepDelayAmount(parseInt(e.target.value) || 0)}
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "4px" }}>
                      Delay Unit
                    </label>
                    <select
                      value={newStepDelayUnit}
                      onChange={(e) => setNewStepDelayUnit(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                      }}
                    >
                      <option value="minutes">Minutes</option>
                      <option value="hours">Hours</option>
                      <option value="days">Days</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                  <button
                    onClick={() => setShowAddStep(false)}
                    style={{
                      padding: "6px 14px",
                      border: "1px solid #d1d5db",
                      borderRadius: "4px",
                      backgroundColor: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddStep}
                    disabled={stepTargetMissing}
                    style={{
                      padding: "6px 14px",
                      backgroundColor: "#000",
                      color: "#fff",
                      border: "none",
                      borderRadius: "4px",
                      cursor: stepTargetMissing ? "not-allowed" : "pointer",
                      opacity: stepTargetMissing ? 0.5 : 1,
                    }}
                  >
                    Add Step
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowAddStep(true)}
              style={{
                marginTop: "12px",
                padding: "8px 16px",
                backgroundColor: "#000",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              + Add Step
            </button>
          )}
        </div>
      )}

      {/* Enrollments Tab */}
      {activeTab === "enrollments" && (
        <div>
          {enrollments.length === 0 ? (
            <p style={{ color: "#6b7280" }}>No enrollments yet.</p>
          ) : (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                backgroundColor: "#fff",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#f9fafb" }}>
                  <th style={{ padding: "10px 16px", textAlign: "left", fontSize: "13px" }}>
                    Email
                  </th>
                  <th style={{ padding: "10px 16px", textAlign: "left", fontSize: "13px" }}>
                    Status
                  </th>
                  <th style={{ padding: "10px 16px", textAlign: "left", fontSize: "13px" }}>
                    Current Step
                  </th>
                  <th style={{ padding: "10px 16px", textAlign: "left", fontSize: "13px" }}>
                    Enrolled At
                  </th>
                </tr>
              </thead>
              <tbody>
                {enrollments.map((enrollment) => (
                  <tr
                    key={enrollment.id}
                    style={{ borderTop: "1px solid #e5e7eb" }}
                  >
                    <td style={{ padding: "10px 16px", fontSize: "14px" }}>
                      {enrollment.subscriber_email}
                    </td>
                    <td style={{ padding: "10px 16px" }}>
                      <span
                        title={
                          enrollment.status === "failed_validation"
                            ? "This email failed validation. It may be invalid or flagged as highly likely to mark messages as spam, so it was excluded from this journey and will not receive emails. To add this person anyway, add them from the Subscribers page with \"Bypass validation\" checked, then enroll them again."
                            : undefined
                        }
                        style={{
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: 500,
                          cursor:
                            enrollment.status === "failed_validation"
                              ? "help"
                              : undefined,
                          backgroundColor:
                            enrollment.status === "active"
                              ? "#f0fdf4"
                              : enrollment.status === "completed"
                              ? "#f0f9ff"
                              : enrollment.status === "failed_validation"
                              ? "#fef2f2"
                              : "#f3f4f6",
                          color:
                            enrollment.status === "active"
                              ? "#166534"
                              : enrollment.status === "completed"
                              ? "#1e40af"
                              : enrollment.status === "failed_validation"
                              ? "#b91c1c"
                              : "#6b7280",
                        }}
                      >
                        {enrollment.status === "failed_validation"
                          ? "failed validation ⓘ"
                          : enrollment.status}
                      </span>
                    </td>
                    <td style={{ padding: "10px 16px", fontSize: "14px" }}>
                      {enrollment.current_step_position}
                    </td>
                    <td style={{ padding: "10px 16px", fontSize: "14px" }}>
                      {new Date(enrollment.enrolled_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {enrollmentsTotalPages > 1 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", margin: "16px 0" }}>
              <button
                onClick={() => loadEnrollments(enrollmentsPage - 1)}
                disabled={enrollmentsPage === 1}
                style={{
                  padding: "6px 12px",
                  borderRadius: "4px",
                  border: "1px solid #d1d5db",
                  backgroundColor: "#fff",
                  color: "#5d5ded",
                  cursor: enrollmentsPage === 1 ? "not-allowed" : "pointer",
                  opacity: enrollmentsPage === 1 ? 0.5 : 1,
                }}
              >
                Previous
              </button>
              <span style={{ fontSize: "14px", color: "#6b7280" }}>
                Page {enrollmentsPage} of {enrollmentsTotalPages}
              </span>
              <button
                onClick={() => loadEnrollments(enrollmentsPage + 1)}
                disabled={enrollmentsPage === enrollmentsTotalPages}
                style={{
                  padding: "6px 12px",
                  borderRadius: "4px",
                  border: "1px solid #d1d5db",
                  backgroundColor: "#fff",
                  color: "#5d5ded",
                  cursor: enrollmentsPage === enrollmentsTotalPages ? "not-allowed" : "pointer",
                  opacity: enrollmentsPage === enrollmentsTotalPages ? 0.5 : 1,
                }}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default JourneyDetail;
